import {Component, OnInit, Optional} from '@angular/core';
import {NgOptimizedImage} from "@angular/common";
import {CardsBasicComponent} from "../../../shared/components/cards/cards-basic/cards-basic.component";
import {MatIcon} from "@angular/material/icon";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {MatIconButton} from "@angular/material/button";
import {MatListItem, MatNavList} from "@angular/material/list";
import {DialogConfirmComponent} from "../../../shared/components/dialogs/dialog-confirm/dialog-confirm.component";
import {Subscription} from "rxjs";
import {SnackbarService} from "../../../shared/services/snackbar.service";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {ImageUploadService} from "../../../shared/services/image-upload.service";
import {MatProgressSpinner} from "@angular/material/progress-spinner";

@Component({
  selector: 'app-media-list',
  standalone: true,
  imports: [
    CardsBasicComponent,
    TranslateModule,
    MatIcon,
    MatIconButton,
    MatListItem,
    MatNavList,
    NgOptimizedImage,
    MatProgressSpinner,
  ],
  templateUrl: './media-list.component.html',
  styleUrl: './media-list.component.css'
})
export class MediaListComponent implements OnInit {
  mediaList: any[] = [];
  imagePreview: string | ArrayBuffer | null = null;
  isLoading = false;
  private readonly SNACKBAR_MESSAGE_SUCCESS = "Media uploaded successfully.";
  private subscriptions: Subscription = new Subscription();

  constructor(
    private imageUploadService: ImageUploadService,
    private _snackbarService: SnackbarService,
    private _translate: TranslateService,
    private _dialog: MatDialog,
    @Optional() private dialogRef?: MatDialogRef<MediaListComponent>
  ) {}

  ngOnInit(): void {
    this.fetchMediaList();
  }

  fetchMediaList(): void {
    this.imageUploadService.listMedia()
      .then((media:any) => {
        this.mediaList = media.reverse();
      })
      .catch((error:any) => {
        console.error("Error fetching media:", error);
      });
  }

  selectImage(filePath: string) {
    if (this.dialogRef) {
      this.dialogRef.close(filePath); // Close the dialog if it was opened as one
    } else {
      // Handle the case when used as a standalone component
      console.log(`Selected image URL: ${filePath}`);
    }
  }

  confirmDelete(fileId: string, event: Event) {
    event.stopPropagation();
    event.preventDefault();

    DialogConfirmComponent.openDeleteDialog(this._dialog, this._translate).subscribe(result => {
      if (result) {
        this.deleteMedia(fileId);
      }
    });
  }

  private deleteMedia(fileId: string): void {
    this.imageUploadService.deleteImage(fileId)
      .then((response:any) => {
        this._snackbarService.show("Media deleted successfully.");
        this.mediaList = this.mediaList.filter(media => media.fileId !== fileId);
      })
      .catch((error:any) => {
        console.error("Error deleting media:", error);
      });
  }


  onDragOver(event: DragEvent): void {
    this.preventAndStopEvent(event);
    event.dataTransfer!.dropEffect = 'copy';
    this.setDragOverStyle(true);
  }

  onDragLeave(event: DragEvent): void {
    this.preventAndStopEvent(event);
    this.setDragOverStyle(false);
  }

  onDrop(event: DragEvent): void {
    this.preventAndStopEvent(event);
    this.setDragOverStyle(false);

    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      this.processFileUpload(event.dataTransfer.files[0]);
    }
  }

  private setDragOverStyle(isDragOver: boolean): void {
    // Optional: Add styling to indicate drag-over state
    const container = document.querySelector('.upload-container') as HTMLElement;
    if (isDragOver) {
      container.classList.add('drag-over');
    } else {
      container.classList.remove('drag-over');
    }
  }

  addMedia(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.processFileUpload(input.files[0]);
    }
  }

  private processFileUpload(file: File): void {
    this.isLoading = true;
    const reader = new FileReader();
    reader.onload = () => {
      this.imagePreview = reader.result;
    };
    reader.readAsDataURL(file);

    this.imageUploadService.uploadImage(file)
      .then((response: any) => {
        this.handleUploadSuccess(response);
      })
      .catch(error => {
        this.handleUploadError(error);
      });
  }

  private handleUploadSuccess(response: any): void {
    this.isLoading = false;
    this.imagePreview = '';
    this._snackbarService.show(this.SNACKBAR_MESSAGE_SUCCESS);
    this.mediaList.unshift(response);
  }

  private handleUploadError(error: any): void {
    this.isLoading = false;
    this.imagePreview = '';
    console.error("Error uploading media:", error);

  }

  private preventAndStopEvent(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }


}
